import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import dayjs from 'dayjs';
import { Alert, Button, Segmented, Space, Tag, Tooltip } from 'antd';
import { PlusOutlined, WarningOutlined } from '@ant-design/icons';
import useQueryParamsContext from '@/contexts/query-params/useQueryParamsContext';
import { API } from '@/services/api.service';
import ColumnTitle from './ColumnTitle';
import PropertyModal from './PropertyModal';
import RecipientModal from './RecipientModal';
import TabTable from './TabTable';
import { errorText, money } from './format';
import { clearedParams, paramsOf } from './listParams';

/** Red when the contract has run out, orange in its last month. */
export const ContractTag = ({ to }) => {
  const { t } = useTranslation('taxReturns');
  if (!to) return null;
  const end = dayjs(to);
  if (end.isBefore(dayjs(), 'day')) return <Tag color="red">{t('people.contractExpired', { date: end.format('DD.MM.YYYY') })}</Tag>;
  if (end.diff(dayjs(), 'day') <= 31) return <Tag color="orange">{t('people.contractEnds', { date: end.format('DD.MM.YYYY') })}</Tag>;
  return <span className="text-xs opacity-70">{end.format('DD.MM.YYYY')}</span>;
};

/**
 * The company's landlords (and other income recipients) and the properties it
 * rents, kept once and picked in every calculation: the month's batch makes
 * one calculation per ticked property with its owners, amounts and accounts.
 * A page at a time from the server; HLB only — the list holds ID numbers.
 */
export default function PeoplePanel({ company, projects }) {
  const { t } = useTranslation('taxReturns');
  const queryClient = useQueryClient();
  const { queryParams, setQueryParams } = useQueryParamsContext();
  const view = queryParams.view === 'recipients' ? 'recipients' : 'properties';
  const isProperties = view === 'properties';
  const params = useMemo(() => paramsOf('people', queryParams), [queryParams]);
  const [propertyModal, setPropertyModal] = useState(null); // { id } | {}
  const [recipientModal, setRecipientModal] = useState(null);

  const listKey = isProperties ? 'tax-properties' : 'tax-recipients';
  const list = useQuery({
    queryKey: [listKey, company, params],
    queryFn: () =>
      isProperties ? API.taxReturns.getProperties(company, params) : API.taxReturns.getRecipients(company, params),
    enabled: Boolean(company),
    // The last page stays up while the next loads, but never the other list's rows under these columns.
    placeholderData: (prev, prevQuery) => (prevQuery?.queryKey?.[0] === listKey ? prev : undefined),
  });
  // Every person, inactive ones too, for the owner pickers of a property.
  const everyone = useQuery({
    queryKey: ['tax-recipients', company, 'all'],
    queryFn: () => API.taxReturns.getRecipients(company, { inactive: true }),
    enabled: Boolean(company),
  });
  const people = everyone.data?.items || [];

  const refresh = () => {
    queryClient.invalidateQueries({ queryKey: ['tax-properties', company] });
    queryClient.invalidateQueries({ queryKey: ['tax-recipients', company] });
  };

  // The two lists have their own sorts; the search and filters carry over.
  const setView = (value) => {
    const sorts = Object.fromEntries(Object.keys(clearedParams('people')).filter((k) => k.startsWith('sort')).map((k) => [k, undefined]));
    setQueryParams({ ...sorts, view: value, offset: 1 });
  };

  const items = list.data?.items || [];
  const propertyColumns = [
    {
      title: () => <ColumnTitle title={t('people.fields.address')} sort="address" search />,
      dataIndex: 'address',
      render: (v, p) => (
        <div>
          <div>
            {v} {!p.active && <Tag>{t('people.inactive')}</Tag>}
          </div>
          <div className="text-xs opacity-60">
            {t(`people.types.${p.type}`)}
            {p.tenants ? ` · ${p.tenants}` : ''}
          </div>
        </div>
      ),
    },
    {
      title: () => <ColumnTitle title={t('fields.project')} sort="project" />,
      dataIndex: 'project',
      width: 170,
      render: (p) => (p ? `${p.code} ${p.name}` : '—'),
    },
    {
      title: t('people.fields.owners'),
      dataIndex: 'owners',
      render: (owners, p) => (
        <Space direction="vertical" size={0}>
          {(owners || []).map((o) => (
            <span key={o.recipient}>
              {o.name}{' '}
              {!o.account && !p.jointPayment && (
                <Tooltip title={t('people.noAccountHint')}>
                  <Tag color="orange" icon={<WarningOutlined />}>
                    {t('people.noAccount')}
                  </Tag>
                </Tooltip>
              )}
            </span>
          ))}
          {p.jointPayment && <Tag>{t('people.joint')}</Tag>}
        </Space>
      ),
    },
    {
      title: () => <ColumnTitle title={t('people.fields.netPerMonth')} sort="netPerMonth" />,
      dataIndex: 'netPerMonth',
      width: 160,
      align: 'right',
      render: (v, p) => `${money(v)} ${p.currency}`,
    },
    { title: t('people.fields.monthsShort'), dataIndex: 'months', width: 80, align: 'right' },
    {
      title: () => <ColumnTitle title={t('people.fields.contractTo')} sort="contractTo" />,
      dataIndex: 'contractTo',
      width: 170,
      render: (v) => <ContractTag to={v} />,
    },
    {
      title: () => <ColumnTitle title={t('people.fields.lastCalc')} sort="lastCalculation" />,
      dataIndex: 'lastCalculation',
      width: 170,
      render: (c) =>
        c ? (
          <div>
            <div>{c.pid}</div>
            {c.coveredPeriod && <div className="text-xs opacity-60">{c.coveredPeriod}</div>}
          </div>
        ) : (
          '—'
        ),
    },
  ];

  const recipientColumns = [
    {
      title: () => <ColumnTitle title={t('people.fields.name')} sort="name" search />,
      dataIndex: 'name',
      render: (v, r) => (
        <div>
          <div>
            {v} {!r.active && <Tag>{t('people.inactive')}</Tag>}
          </div>
          {r.address && <div className="text-xs opacity-60">{r.address}</div>}
        </div>
      ),
    },
    {
      title: () => <ColumnTitle title={t('people.fields.idNumber')} sort="idNumber" />,
      dataIndex: 'idNumber',
      width: 170,
      render: (v, r) => (v ? `${v}${r.idType === 'passport' ? ` (${t('people.idTypes.passport')})` : ''}` : '—'),
    },
    {
      title: () => <ColumnTitle title={t('people.fields.account')} sort="account" />,
      dataIndex: 'account',
      width: 240,
      render: (v, r) =>
        v ? (
          <div>
            <div>{v}</div>
            {r.accountHolder && <div className="text-xs opacity-60">{r.accountHolder}</div>}
          </div>
        ) : (
          <Tag color="orange" icon={<WarningOutlined />}>
            {t('people.noAccount')}
          </Tag>
        ),
    },
    {
      title: t('people.fields.properties'),
      dataIndex: 'properties',
      render: (props) => (props || []).map((p) => p.address).join('; ') || '—',
    },
    {
      title: () => <ColumnTitle title={t('fields.project')} sort="project" />,
      dataIndex: 'project',
      width: 170,
      render: (p) => (p ? `${p.code} ${p.name}` : '—'),
    },
  ];

  return (
    <Space direction="vertical" size="middle" style={{ width: '100%' }}>
      <p className="text-sm opacity-70">{t('people.help')}</p>
      <Segmented
        value={view}
        onChange={setView}
        options={[
          { value: 'properties', label: t('people.tabs.properties') },
          { value: 'recipients', label: t('people.tabs.recipients') },
        ]}
      />
      {list.isError && <Alert type="error" showIcon message={errorText(t, list.error)} />}

      <TabTable
        tab="people"
        title={`${t(isProperties ? 'people.tabs.properties' : 'people.tabs.recipients')} (${list.data?.total ?? 0})`}
        data={items}
        total={list.data?.count || 0}
        status={list.isLoading ? 'pending' : list.isError ? 'error' : 'success'}
        columns={isProperties ? propertyColumns : recipientColumns}
        projects={projects}
        actions={
          <Button
            shape="round"
            className="h-8"
            icon={<PlusOutlined />}
            onClick={() => (isProperties ? setPropertyModal({}) : setRecipientModal({}))}
          >
            {t(isProperties ? 'people.addProperty' : 'people.addRecipient')}
          </Button>
        }
        onRow={(r) => ({
          onClick: () => (isProperties ? setPropertyModal({ id: r._id }) : setRecipientModal({ id: r._id })),
          style: { cursor: 'pointer' },
        })}
      />

      <PropertyModal
        open={Boolean(propertyModal)}
        company={company}
        property={propertyModal?.id ? items.find((p) => p._id === propertyModal.id) : null}
        recipients={people}
        projects={projects}
        onAddRecipient={() => setRecipientModal({})}
        onClose={() => setPropertyModal(null)}
        onSaved={refresh}
      />
      <RecipientModal
        open={Boolean(recipientModal)}
        company={company}
        recipient={recipientModal?.id ? people.find((r) => r._id === recipientModal.id) : null}
        projects={projects}
        onClose={() => setRecipientModal(null)}
        onSaved={refresh}
      />
    </Space>
  );
}
